/**
 * Animated Search Placeholder
 * Cycles through different search term suggestions in the search input placeholder
 */

class AnimatedSearchPlaceholder {
  constructor(config = {}) {
    this.searchTerms = config.searchTerms || [
      "Skincare",
      "Supplements",
      "Brands",
      "Vitamins",
      "Protein",
      "Wellness",
    ];
    this.prefix = config.prefix || "Search for ";
    this.typingSpeed = config.typingSpeed || 100; // ms per character
    this.deletingSpeed = config.deletingSpeed || 50; // ms per character
    this.pauseDuration = config.pauseDuration || 2000; // ms to pause on complete word

    this.currentTermIndex = 0;
    this.currentCharIndex = 0;
    this.isDeleting = false;
    /** @type {Set<HTMLInputElement>} */
    this.inputElements = new Set();
    this.animationTimeout = null;
    this.observerTimeout = null;

    this.init();
  }

  /**
   * Initialize the animated placeholder
   */
  init() {
    // Try to find the input immediately
    this.findAndAttachToInput();

    // Also set up a MutationObserver to watch for dynamically added inputs
    this.observeDOMForInput();
  }

  /**
   * Find the Snize search input and start animation
   */
  findAndAttachToInput() {
    // Try multiple selectors to find the Snize search input
    const selector = "#header-search-input";

    // Removed console.log for performance

    let foundAny = false;

    const nodeList = document.querySelectorAll(selector);
    if (!nodeList.length) return;

    nodeList.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (this.inputElements.has(input)) return;

      this.inputElements.add(input);
      // For backward compatibility, keep the first-found as primary
      if (!this.inputElement) {
        this.inputElement = input;
      }
      this.startAnimationForInput(input);
      foundAny = true;
    });

    return foundAny;
  }

  /**
   * Watch for the Snize input to be added to the DOM
   */
  observeDOMForInput() {
    // Clear any existing observer timeout
    if (this.observerTimeout) {
      clearTimeout(this.observerTimeout);
    }

    let attempts = 0;
    const maxAttempts = 10; // Try for 5 seconds (10 * 500ms) - reduced from 40

    const tryFinding = () => {
      attempts++;
      if (!this.inputElement && attempts < maxAttempts) {
        // Removed console.log to reduce overhead
        const found = this.findAndAttachToInput();
        if (!found) {
          this.observerTimeout = setTimeout(tryFinding, 500);
        }
      } else if (attempts >= maxAttempts) {
        // Silently stop - no console spam
      }
    };

    // Start trying after a delay to not block initial page load
    this.observerTimeout = setTimeout(tryFinding, 1000);

    // Set up MutationObserver
    const observer = new MutationObserver((mutations) => {
      if (this.inputElement) {
        observer.disconnect();
        return;
      }

      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0) {
          const found = this.findAndAttachToInput();
          if (found) {
            observer.disconnect();
            if (this.observerTimeout) {
              clearTimeout(this.observerTimeout);
            }
            break;
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Start the typing animation for a specific input
   * @param {HTMLInputElement} input
   */
  startAnimationForInput(input) {
    // Store original placeholder on the element itself
    if (!input.dataset.originalPlaceholder) {
      input.dataset.originalPlaceholder = input.placeholder || "";
    }

    // Don't animate if user has focused the input or typed something
    input.addEventListener("focus", () => {
      this.stopAnimation();
      if (!input.value) {
        input.placeholder = this.prefix + "products...";
      }
    });

    input.addEventListener("blur", () => {
      if (!input.value) {
        this.currentCharIndex = 0;
        this.isDeleting = false;
        this.animate();
      }
    });

    // Start the typing effect after a small delay to not block page load
    setTimeout(() => this.animate(), 500);
  }

  /**
   * Main animation loop
   */
  animate() {
    if (!this.inputElements.size) {
      return;
    }

    // If all inputs are focused or have values, pause the animation
    const hasIdleInput = Array.from(this.inputElements).some(
      (input) => input !== document.activeElement && !input.value,
    );
    if (!hasIdleInput) {
      return;
    }

    const currentTerm = this.searchTerms[this.currentTermIndex];

    if (this.isDeleting) {
      // Deleting characters
      this.currentCharIndex--;

      if (this.currentCharIndex <= 0) {
        // Finished deleting, move to next term
        this.isDeleting = false;
        this.currentTermIndex =
          (this.currentTermIndex + 1) % this.searchTerms.length;
        this.animationTimeout = setTimeout(() => this.animate(), 500);
        return;
      }
    } else {
      // Typing characters
      this.currentCharIndex++;

      if (this.currentCharIndex > currentTerm.length) {
        // Finished typing, pause then start deleting
        this.isDeleting = true;
        this.animationTimeout = setTimeout(
          () => this.animate(),
          this.pauseDuration,
        );
        return;
      }
    }

    // Update placeholder
    const displayText = currentTerm.substring(0, this.currentCharIndex);
    const newPlaceholder = this.prefix + `'${displayText}'`;

    // Update all idle inputs
    this.inputElements.forEach((input) => {
      if (input === document.activeElement || input.value) return;
      input.placeholder = newPlaceholder;
      input.setAttribute("placeholder", newPlaceholder);
    });

    // Continue animation
    const speed = this.isDeleting ? this.deletingSpeed : this.typingSpeed;
    this.animationTimeout = setTimeout(() => this.animate(), speed);
  }

  /**
   * Stop the animation
   */
  stopAnimation() {
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = null;
    }
  }

  /**
   * Destroy the instance and clean up
   */
  destroy() {
    this.stopAnimation();
    if (this.observerTimeout) {
      clearTimeout(this.observerTimeout);
    }
    // No longer using a MutationObserver for placeholder changes
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.animatedSearchPlaceholder = new AnimatedSearchPlaceholder({
      searchTerms: [
        "Skincare",
        "Supplements",
        "Brands",
        "Vitamins",
        "Protein",
        "Wellness",
        "Beauty",
        "Nutrition",
      ],
      prefix: "Search for ",
      typingSpeed: 100,
      deletingSpeed: 50,
      pauseDuration: 2000,
    });
  });
} else {
  window.animatedSearchPlaceholder = new AnimatedSearchPlaceholder({
    searchTerms: [
      "Skincare",
      "Supplements",
      "Brands",
      "Vitamins",
      "Protein",
      "Wellness",
      "Beauty",
      "Nutrition",
    ],
    prefix: "Search for ",
    typingSpeed: 100,
    deletingSpeed: 50,
    pauseDuration: 2000,
  });
}
